<template>
  <main-template>
    <v-row class="mb-2">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Permissões de Registro Remoto
        </h1>
        <p class="body-2 ml-8 mb-0">
          Escolha quem pode pedir registro de ponto remoto e em quais dias da semana.
        </p>
      </v-col>
    </v-row>

    <v-alert type="info" text dense class="mb-4">
      Por padrão todos podem pedir em qualquer dia. As regras valem para o pedido feito pelo próprio
      colaborador: quando um gestor lança o registro em nome dele, não há restrição. Ninguém altera
      a própria regra.
    </v-alert>

    <v-card outlined>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="busca"
              label="Buscar colaborador"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filtroSituacao"
              :items="opcoesSituacao"
              label="Situação"
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="colaboradoresFiltrados"
        :loading="carregando"
        :items-per-page="25"
        loading-text="Carregando colaboradores..."
        no-data-text="Nenhum colaborador para configurar."
        no-results-text="Nenhum colaborador encontrado."
      >
        <template v-slot:item.permitido="{ item }">
          <v-chip
            small
            :color="item.permitido ? 'green lighten-4' : 'red lighten-4'"
            :text-color="item.permitido ? 'green darken-3' : 'red darken-3'"
            class="font-weight-bold"
          >
            {{ item.permitido ? "Liberado" : "Bloqueado" }}
          </v-chip>
        </template>

        <template v-slot:item.dias_semana="{ item }">
          <span v-if="!item.permitido" class="grey--text">—</span>
          <span v-else-if="!item.dias_semana.length" class="grey--text">Todos os dias</span>
          <template v-else>
            <v-chip v-for="d in item.dias_semana" :key="d" x-small class="mr-1 mb-1">
              {{ nomeCurto(d) }}
            </v-chip>
          </template>
        </template>

        <template v-slot:item.atualizado_em="{ item }">
          <div v-if="item.atualizado_em">
            <div>{{ formatarDataHora(item.atualizado_em) }}</div>
            <div class="text-caption grey--text">{{ item.atualizado_por }}</div>
          </div>
          <span v-else class="grey--text">Padrão</span>
        </template>

        <template v-slot:item.acoes="{ item }">
          <v-btn small text color="primary" @click="abrirEdicao(item)">
            <v-icon left small>mdi-pencil-outline</v-icon>
            Editar
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="520" persistent>
      <v-card v-if="editando">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Registro remoto: {{ editando.nome }}
        </v-card-title>
        <v-card-text>
          <v-switch
            v-model="form.permitido"
            inset
            :label="form.permitido ? 'Liberado para registrar remoto' : 'Bloqueado'"
            class="mt-0"
          />

          <template v-if="form.permitido">
            <div class="subtitle-2 font-weight-bold mb-1">Dias permitidos</div>
            <div class="text-caption grey--text mb-2">
              Sem dias marcados = pode em qualquer dia.
              <span v-if="editando.dias_trabalho.length">
                Só aparecem habilitados os dias em que a pessoa trabalha pela escala.
              </span>
              <span v-else>A pessoa não tem escala vigente; todos os dias estão disponíveis.</span>
            </div>
            <div class="d-flex flex-wrap">
              <v-checkbox
                v-for="d in ordemDias"
                :key="d"
                v-model="form.dias_semana"
                :value="d"
                :label="nomeCurto(d)"
                :disabled="!diaNaEscala(d)"
                dense
                hide-details
                class="mr-4 mt-0"
              />
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="salvando" @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="salvando" @click="salvar">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
// 0 = domingo ... 6 = sábado (mesma numeração da API); exibe começando na segunda
const NOMES_CURTOS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default {
  name: "RegistroRemotoRegrasPage",
  data() {
    return {
      carregando: false,
      salvando: false,
      colaboradores: [],
      busca: "",
      filtroSituacao: "todos",
      opcoesSituacao: [
        { text: "Todos", value: "todos" },
        { text: "Liberados", value: "liberados" },
        { text: "Bloqueados", value: "bloqueados" },
        { text: "Com dias restritos", value: "restritos" },
      ],
      ordemDias: [1, 2, 3, 4, 5, 6, 0],
      dialog: false,
      editando: null,
      form: { permitido: true, dias_semana: [] },
      headers: [
        { text: "Colaborador", value: "nome" },
        { text: "Situação", value: "permitido" },
        { text: "Dias permitidos", value: "dias_semana", sortable: false },
        { text: "Última alteração", value: "atualizado_em" },
        { text: "", value: "acoes", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    colaboradoresFiltrados() {
      const termo = (this.busca || "").trim().toLowerCase();
      return this.colaboradores.filter((c) => {
        if (this.filtroSituacao === "liberados" && !c.permitido) return false;
        if (this.filtroSituacao === "bloqueados" && c.permitido) return false;
        if (this.filtroSituacao === "restritos" && !(c.permitido && c.dias_semana.length)) {
          return false;
        }
        return !termo || (c.nome || "").toLowerCase().includes(termo);
      });
    },
  },
  mounted() {
    this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const resposta = await this.$services.registroRemotoRegrasService.listar();
        this.colaboradores = resposta.colaboradores || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },

    abrirEdicao(item) {
      this.editando = item;
      this.form = { permitido: item.permitido, dias_semana: [...item.dias_semana] };
      this.dialog = true;
    },

    diaNaEscala(dia) {
      const dias = this.editando?.dias_trabalho || [];
      return !dias.length || dias.includes(dia);
    },

    async salvar() {
      this.salvando = true;
      try {
        const atualizado = await this.$services.registroRemotoRegrasService.salvar(
          this.editando.id,
          {
            permitido: this.form.permitido,
            dias_semana: this.form.permitido ? this.form.dias_semana : [],
          }
        );
        this.colaboradores = this.colaboradores.map((c) =>
          c.id === atualizado.id ? atualizado : c
        );
        this.$toast.success("Regra de registro remoto salva.");
        this.dialog = false;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },

    nomeCurto(dia) {
      return NOMES_CURTOS[dia];
    },

    formatarDataHora(iso) {
      return new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>
